/* eslint-disable react/prop-types */
import React from 'react';
import {graphql, Slice} from 'gatsby';
import {getI18n, useTranslation} from 'react-i18next';
import PeoplePre from '../components/shared/PeoplePre';
import CkEditor from '../components/shared/ckeditor/CkEditor';
import * as PeoplePreStyles from '../styles/internal-page/contact-card.module.scss';
import InternalPageSimpleLayoutNew from '../components/layout/templates/InternalPageSimpleLayoutNew';
import '../services/i18n';
import { searcherGenerateUrl, setupi18n } from '../utils/utils';
import {
  getImageAndAlt,
  getSEOInformations
} from '../utils/parsing';
import Seo from '../seo/seo';
import PolyLink from '../components/shared/PolyLink';
import clsx from 'clsx';


const getCanonicalLinkPrefix = (researcher) => {
  if (researcher?.affiliation) {
    for(const af of researcher.affiliation) {
      if (af?.organization?.url) {
        return af?.organization?.url;
      }
    }
  }
  return null;
};


export const Head = ({ data, pageContext, location }) => {
  const {locale, generalSeo, websiteSeo, name } = pageContext;
  const {
    pageSeo,
    metaDescription,
  } = getSEOInformations(data);

  const researcher = data.researcher;
  const photo = getImageAndAlt(researcher.photo, locale);

  const canonicalLinkPrefix = getCanonicalLinkPrefix(researcher);
  let canonical_link = null;
  if (canonicalLinkPrefix) {
    canonical_link = canonicalLinkPrefix;
    if (!canonicalLinkPrefix.endsWith('/')) {
      canonical_link = canonical_link +'/';
    }
    canonical_link = `${canonical_link}${locale === 'fr'?'annuaire': 'directory'}/${researcher?.slug}`;
  }

  return (
    <Seo
      canonicalLink={canonical_link}
      coverMedia={photo}
      generalSeo={generalSeo}
      language={locale}
      location={location}
      metaDescription={metaDescription || researcher?.short_biography}
      pageSeo={pageSeo}
      publishedAt={researcher.publishedAt}
      type={pageContext.pageType}
      website={name}
      websiteSeo={websiteSeo}
    />
  );
};

export default function ResearcherPage ({data, pageContext}) {
  const {
    locale,
    breadcrumbs
  } = pageContext;
  setupi18n(locale, getI18n());
  const {t} = useTranslation();
  const researcher = data.researcher;
  const related_contents = researcher.identifier && researcher.identifier.length > 0 && [
    {
      'content': [
        {
          '__typename': 'CustomInfoComponent',
          'id': '2873',
          'thumbnailColor': 'RED',
          'comps':
          (
            <ul>
              {researcher.identifier.map((e, i) => (
                <li key={'related' + i}>
                  <a
                    className={PeoplePreStyles.linkInfo}
                    href={searcherGenerateUrl(e)}
                    rel="noreferrer"
                    target="_blank"
                    title={t(`research.info_url.${e.type}`) + ' - ' + t('polylink.blank')}
                  >
                    {t(`research.info_url.${e.type}`)}
                  </a>
                </li>
              ))}
            </ul>
          ),
          'title': t('research.people_pre.more_info'),
        }
      ]
    }
  ];
  const sections = [
    {
      title: 'research.people_pre.biography',
      content: researcher.biography,
      render: (content) => <CkEditor html={content} />
    },
    {
      title: 'research.people_pre.research',
      content: researcher.research_wip,
      render: (content) => <CkEditor html={content} />
    },
    {
      title: 'research.people_pre.projects',
      content: researcher.projects,
      render: (content) => <CkEditor html={content} />
    },
    {
      title: 'research.people_pre.thesis',
      content: researcher.thesis,
      render: (content) => <CkEditor html={content} />
    },
    {
      title: 'research.people_pre.thesislive',
      content: researcher.thesis_wip,
      render: (content) => <CkEditor html={content} />
    },
    {
      title: 'research.people_pre.enseigne',
      content: researcher.teachings,
      render: (content) => <CkEditor html={content} />
    },
    {
      title: 'research.people_pre.awards',
      content: researcher.awards,
      render: (content) => <CkEditor html={content} />
    },
    {
      title: 'research.people_pre.edit_resps',
      content: researcher.edit_resps,
      render: (content) => <CkEditor html={content} />
    },
    {
      title: 'research.people_pre.publications',
      content: researcher.publications,
      render: (content) => <CkEditor html={content} />
    },
  ];
  return (
    <Slice alias={'internal-page-layout'}>
      <InternalPageSimpleLayoutNew
        filAriane={breadcrumbs}
        related_contents={related_contents}
      >
        <div
          className={clsx({
            [PeoplePreStyles.researcherPage]: related_contents,
            [PeoplePreStyles.researcherPageRelated]: !related_contents
          })}
        >
          <PeoplePre researcher={researcher} />
          {sections.map((section, index) => (
            section.content && (
              <div
                key={'section' + index}
                style={{'clear': 'both'}}
              >
                <h2>{t(section.title)}</h2>
                {section.render(section.content)}
              </div>
            )
          ))}
        </div>
      </InternalPageSimpleLayoutNew>
    </Slice>
  );
}

export const query = graphql`
  query (
    $personId: String!,
  ) {
    researcher: cmsRsDirPeople(id: { eq: $personId }) {
      ...ResDirResearcher
      photo {
        ...ResDirMediaFragment
      }
    }
  }
`;
